import React, { useState, ChangeEvent } from 'react';
import { fetchGatewayStatus } from '../services/backend/SrcfulPublicApi';
import PageContainer from './layout/PageContainer';
import InfoRow from './InfoRow';
import { Link } from '@tanstack/react-router';
import { WrenchScrewdriverIcon } from "@heroicons/react/24/solid";

const GatewayStatusCheck: React.FC = () => {
  const [gatewayId, setGatewayId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<{ exists: boolean; wallet: boolean; location: boolean } | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setGatewayId(e.target.value);
  };

  const handleCheck = async () => {
    if (!gatewayId.trim()) {
      setError('Please enter a gateway ID');
      return;
    }

    setLoading(true);
    setError(null);
    setStatus(null);

    try {
      const statusResult = await fetchGatewayStatus(gatewayId);
      setStatus(statusResult);
    } catch (err) {
      setError('Failed to fetch gateway status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageContainer>
        <div className="mb-6 sm:mb-8 mt-1 sm:mt-1">
          <div className="flex items-center gap-2 mb-2 sm:mb-4">
            <div className="flex items-center gap-2">
              <WrenchScrewdriverIcon className="w-5 h-5 text-[#00FF84]" />
              <h1 className="text-xl font-bold text-white">
                Gateway Status Check
              </h1>
            </div>
          </div>
        </div>

        <div className="w-full">
          <div>
            <p className="text-gray-400">
              Enter a gateway ID to check its registration status and configuration.
            </p>
          </div>

          <div className="mt-5 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <input
                type="text"
                className="w-full sm:max-w-xl px-4 py-2 bg-[#1D1D1D] border border-[#292929] rounded-[8px] text-gray-200 focus:outline-none focus:border-[#00FF84]"
                placeholder="Enter Gateway ID"
                value={gatewayId}
                onChange={handleChange}
              />
              <button
                className={`whitespace-nowrap w-full sm:w-auto px-6 py-2 rounded-[8px] bg-[#00FF84] text-black font-medium hover:bg-green-500 transition-colors
                  ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={handleCheck}
                disabled={loading}
              >
                {loading ? 'Checking...' : 'Check Status'}
              </button>
            </div>

            {error && (
              <div className="p-4 bg-[#1D1D1D] border border-red-700 rounded-[8px] text-red-200">
                {error}
              </div>
            )}

            {status && !status.exists && (
              <div className="p-4 bg-[#1D1D1D] border border-yellow-700 rounded-[8px] text-yellow-200">
                <h3 className="font-semibold mb-2">Gateway Not Found</h3>
                <p>This gateway needs to be added before it can be onboarded. Please:</p>
                <ol className="list-decimal ml-6 mt-2 space-y-2">
                  <li>Verify you have the correct Gateway ID</li>
                  <li>
                    Use the <Link 
                      to="/connect-gw" 
                      className="text-blue-400 hover:text-blue-300 underline"
                    >
                      Gateway Information
                    </Link> tool to get the gateway ID and public key
                  </li>
                  <li>
                    Contact support with the following information:
                    <ul className="list-disc ml-6 mt-1">
                      <li>Gateway ID</li>
                      <li>Gateway Public Key</li>
                    </ul>
                  </li>
                </ol>
              </div>
            )}

            {status && status.exists && (
              <div className="mt-5 divide-y divide-[#292929]">
                <InfoRow
                  label="Gateway ID"
                  value={gatewayId}
                  isCopyable={true}
                  tooltip="The unique identifier of the gateway"
                />
                <InfoRow
                  label="Wallet Status"
                  value={status.wallet ? 'Assigned' : 'Not Assigned'}
                  displayValue={
                    <span className={status.wallet ? 'text-green-500' : 'text-yellow-500'}>
                      {status.wallet ? 'Assigned' : 'Not Assigned'}
                    </span>
                  }
                  tooltip="Whether the gateway is assigned to a wallet"
                />
                <InfoRow
                  label="Location Status"
                  value={status.location ? 'Set' : 'Not Set'}
                  displayValue={
                    <span className={status.location ? 'text-green-500' : 'text-yellow-500'}>
                      {status.location ? 'Set' : 'Not Set'}
                    </span>
                  }
                  tooltip="Whether the gateway has a location set"
                />
              </div>
            )}
          </div>
        </div>
      </PageContainer>
    </>
  );
};

export default GatewayStatusCheck; 